import React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ShieldAlert,
  Users,
  ArrowRightCircle,
  PauseCircle,
  Activity,
  PlayCircle,
  Lock,
  Split, // Added for the "Two Paths" icon
} from "lucide-react";

const ProgramLaunchCard = () => {
  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden animate-in fade-in duration-500">
      {/* 1. EXECUTIVE HEADER */}
      <div className="p-5 border-b border-stone-100">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="text-lg font-bold text-stone-800 flex items-center gap-2">
              🚀 Program Launch Control Tower
            </h3>
            <p className="text-xs text-stone-400 font-medium uppercase tracking-widest mt-1">
              Status: Dependency Gated (Internal Readiness: 100%)
            </p>
          </div>
          <div className="bg-amber-50 text-amber-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-amber-100 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" />
            Controlled Hold
          </div>
        </div>

        <p className="text-sm text-stone-600 leading-relaxed mt-2">
          <strong>Executive Summary:</strong> We are in a controlled hold due to
          external dependencies. Internal readiness is complete. Supplier
          activity is{" "}
          <span className="underline decoration-stone-300 decoration-2 underline-offset-2">
            intentionally paused
          </span>{" "}
          to prevent premature engagement and protect partner trust.
        </p>
      </div>

      {/* 2. STAKEHOLDER DEPENDENCY MAP */}
      <div className="bg-stone-50/50 p-5 border-b border-stone-100">
        <h4 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4 flex items-center gap-2">
          <Users className="w-3 h-3" /> External Dependency Map
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* MARLEE - Decision Needed */}
          <div className="bg-white p-3 rounded-lg border border-amber-200 shadow-sm flex items-start gap-3">
            <div className="mt-0.5 text-amber-500">
              <ArrowRightCircle className="w-4 h-4" />
            </div>
            <div>
              <div className="flex justify-between items-center w-full">
                <span className="text-xs font-bold text-stone-700">
                  Marlee (US)
                </span>
                <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                  Decision Requested
                </span>
              </div>
              <p className="text-xs text-stone-500 mt-1 leading-snug">
                Presented "Controlled Optionality" (Integration vs CSV).
                Awaiting preference.
              </p>
            </div>
          </div>

          {/* SHATAKSHI - Stabilized */}
          <div className="bg-white p-3 rounded-lg border border-orange-200 shadow-sm flex items-start gap-3">
            <div className="mt-0.5 text-orange-500">
              <Activity className="w-4 h-4" />
            </div>
            <div>
              <div className="flex justify-between items-center w-full">
                <span className="text-xs font-bold text-stone-700">
                  Shatakshi (India)
                </span>
                <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                  Stabilized
                </span>
              </div>
              <p className="text-xs text-stone-500 mt-1 leading-snug">
                Proactive status sent. Monitoring for alignment reset.
              </p>
            </div>
          </div>

          {/* LUIS - Strategy Active */}
          <div className="bg-white p-3 rounded-lg border border-blue-200 shadow-sm flex items-start gap-3">
            <div className="mt-0.5 text-blue-500">
              <Split className="w-4 h-4" /> {/* CHANGED ICON: Split paths */}
            </div>
            <div>
              <div className="flex justify-between items-center w-full">
                <span className="text-xs font-bold text-stone-700">
                  Luis (Mkt Ops)
                </span>
                <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                  Paths Ready
                </span>
              </div>
              <p className="text-xs text-stone-500 mt-1 leading-snug">
                <strong>Strategy:</strong> Two paths configured (Direct Test
                Lead OR Secure CSV). Ready for either.
              </p>
            </div>
          </div>

          {/* CONVERTR - On Track */}
          <div className="bg-white p-3 rounded-lg border border-emerald-200 shadow-sm flex items-start gap-3">
            <div className="mt-0.5 text-emerald-500">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div>
              <div className="flex justify-between items-center w-full">
                <span className="text-xs font-bold text-stone-700">
                  Convertr (Patrick)
                </span>
                <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                  On Track
                </span>
              </div>
              <p className="text-xs text-stone-500 mt-1 leading-snug">
                Monitoring progress. ETA confirmation pending unlock.
              </p>
            </div>
          </div>

          {/* SUPPLIERS - HELD */}
          <div className="bg-stone-100 p-3 rounded-lg border border-stone-200 shadow-inner flex items-start gap-3 col-span-1 md:col-span-2 opacity-80">
            <div className="mt-0.5 text-stone-500">
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <div className="flex justify-between items-center w-full">
                <span className="text-xs font-bold text-stone-700">
                  Suppliers
                </span>
                <span className="text-[10px] font-bold bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded border border-stone-300">
                  Intentionally Held
                </span>
              </div>
              <p className="text-xs text-stone-500 mt-1 leading-snug">
                <strong>Strategy:</strong> Prevent premature uploads to avoid
                rework. Gated until Convertr unlocks.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 3. TACTICAL ACTIONS */}
      <div className="p-5">
        <h4 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">
          My Action Ledger
        </h4>
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-xs text-stone-400 line-through">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span>Send clear integration test request to Marlee</span>
          </div>

          <div className="flex items-center gap-3 text-sm text-stone-700 bg-blue-50/50 p-2 rounded border border-blue-100">
            <Split className="w-4 h-4 text-blue-500" />
            <span>
              Present "Two Path" Options (Integration vs CSV) to Stakeholders
            </span>
          </div>

          <div className="flex items-center gap-3 text-sm text-stone-700 bg-stone-50 p-2 rounded border border-stone-100">
            <Clock className="w-4 h-4 text-stone-400" />
            <span>Acknowledge Convertr progress & ask ETA (Pending)</span>
          </div>

          <div className="flex items-center gap-3 text-sm text-stone-400 italic pl-2">
            <PauseCircle className="w-4 h-4" />
            <span>Draft supplier update (Intentionally deferred)</span>
          </div>
        </div>
      </div>

      {/* 4. EXECUTION INSIGHT */}
      <div className="bg-stone-800 p-4 text-stone-300 text-xs leading-relaxed border-t border-stone-200">
        <div className="flex items-center gap-2 mb-1 text-stone-100 font-bold uppercase tracking-wider text-[10px]">
          <ShieldAlert className="w-3 h-3" /> Execution Insight
        </div>
        <p>
          Internal readiness is complete. Current delay is{" "}
          <span className="text-white">dependency-driven</span>. We have
          restored optionality by offering a CSV path to prevent Integration
          from being a hard blocker.
        </p>
      </div>
    </div>
  );
};

export default ProgramLaunchCard;
